{
  "page": {
    "refreshed": "Refreshed {{when}}",
    "failedToLoad": "Failed to load analytics data:",
    "initializing": "Analytics is initializing",
    "initializingBody": "Analytics is preparing its first snapshot. This page will refresh automatically when data is ready (typically within 60 seconds).",
    "outdatedTitle": "Analytics data may be outdated",
    "outdatedBody": "Last successful refresh: {{when}}. Last attempt failed: {{error}}",
    "refreshedUnknown": "unknown",
    "noWorkspacesTitle": "No {{type}} workspaces yet",
    "noWorkspacesBody": "This service type has no workspaces to aggregate. Switch service types from the selector above, or check back once the first one is created."
  },
  "filters": {
    "preset7d": "7d",
    "preset30d": "30d",
    "preset90d": "90d",
    "preset1yr": "1 year",
    "custom": "Custom",
    "customRange": "Custom: {{from}} – {{to}}",
    "customDateRange": "Custom date range",
    "from": "From",
    "to": "To",
    "apply": "Apply",
    "clear": "Clear",
    "all": "All",
    "filterColumn": "Filter {{label}}",
    "filterAria": "Filter {{label}}",
    "noOptions": "No options",
    "clearFilters": "Clear filters",
    "sortLabel": "Sort: {{column}} {{direction}}"
  },
  "time": {
    "justNow": "just now",
    "minAgo": "{{count}} min ago",
    "hourAgo_one": "{{count}} hour ago",
    "hourAgo_other": "{{count}} hours ago",
    "dayAgo_one": "{{count}} day ago",
    "dayAgo_other": "{{count}} days ago",
    "minAgoShort": "{{count}}m ago",
    "hourAgoShort": "{{count}}h ago",
    "dayAgoShort": "{{count}}d ago"
  },
  "kpi": {
    "totalWorkspaces": "Total Workspaces",
    "totalWorkspaces_validation": "Total Validations",
    "totalWorkspaces_auditing": "Total Audits",
    "totalWorkspaces_development": "Total Developments",
    "totalWorkspacesTooltip": "Workspace count for the service type selected above. Choose All Service Types for the portfolio-wide total.",
    "completed": "Completed",
    "completedSubClean": "{{clean}} clean · {{withErrors}} with errors",
    "completedSubPercent": "{{percent}}% of total",
    "totalIssues": "Total Issues",
    "issuesBySeverity": "T1: {{t1}} · T2: {{t2}} · T3: {{t3}} · T4: {{t4}}",
    "timeSaved": "Time Saved",
    "timeSavedNoData": "Not available — completion time isn't recorded",
    "timeSavedSub_one": "≈ {{weeks}} FTE-week · based on {{count}} measured workspace",
    "timeSavedSub_other": "≈ {{weeks}} FTE-weeks · based on {{count}} measured workspaces",
    "timeSavedSubWeeksOnly": "≈ {{weeks}} FTE-weeks",
    "timeSavedTooltip": "An estimate built on an assumed baseline. For each completed workspace we take 120 hours — the assumed cost of one full validation without this pipeline, covering planning, data preparation, analytics and drafting — and subtract the time actually taken from the workspace being created to it reaching its final state, then add the results together. The 120 hours is a flat assumption applied to every workspace, not a figure measured per engagement. Only workspaces that reached their final state after this measurement began are included. Workspaces that already existed when it began are marked as predating it and stay excluded permanently — their finish time was never recorded and is never estimated after the fact — so this covers part of the portfolio rather than all of it. The line beneath the figure says how many workspaces it is based on.",
    "timeSavedTooltipNoData": "Not currently available. Reporting time saved requires knowing when each workspace finished, and no completed workspace in this view carries a recorded finish time, so there is nothing to measure against.",
    "totalRuns30d": "Total runs (30d)",
    "activeRuns": "Active runs",
    "successRate": "Success rate",
    "avgTime": "Avg time"
  },
  "severity": {
    "fallback": "Type {{n}}"
  },
  "sections": {
    "portfolioTitle": "Portfolio Overview",
    "portfolioDescription": "Models per top service area, and their progress",
    "issuesOverviewTitle": "Issues & Risk Overview",
    "issuesOverviewDescription": "Severity distribution across the portfolio and per-model breakdown",
    "workspacePipelineTitle": "Workspace Pipeline",
    "workspacePipelineDescription": "All workspaces — section progress, data gaps, and issues per workspace",
    "issuesDetailsTitle": "Issues & Risk Details",
    "issuesDetailsDescription": "Root-cause issues with supporting symptoms and workspace links",
    "toolboxUsageTitle": "Toolbox Usage",
    "toolboxUsageDescription": "Tool runs across the workspace toolbox",
    "consumptionTitle": "Token Consumption",
    "consumptionDescription": "LLM token usage this month — across all workspaces and toolbox runs",
    "recentActivityTitle": "Recent Activity",
    "recentActivityDescription": "Last 24 hours of workflow state transitions"
  },
  "chart": {
    "rendering": "Drawing chart…",
    "renderStalled": "This chart is taking longer than usual to draw. The figures below are unaffected."
  },
  "portfolio": {
    "byVertical": "By Vertical",
    "byProgress": "By Progress",
    "byServiceType": "By Service Type",
    "serviceTypeLabels": {
      "validation": "Validation",
      "auditing": "Auditing",
      "development": "Development"
    },
    "noWorkspaces": "No workspaces to display.",
    "modelCount_one": "{{count}} model",
    "modelCount_other": "{{count}} models",
    "tooltipComplete": "{{completed}} / {{total}} complete ({{percent}}%)",
    "tooltipPortfolioShare": "{{percent}}% of portfolio",
    "loop": {
      "setup": "Setup",
      "plan": "Plan",
      "data_prep": "Data Prep",
      "report_gen": "Report Gen",
      "review": "Review",
      "test_run": "Test Run",
      "complete": "Complete",
      "failed": "Failed"
    },
    "modelsLine_one": "{{count}} model ({{percent}}% of {{name}})",
    "modelsLine_other": "{{count}} models ({{percent}}% of {{name}})"
  },
  "issues": {
    "bySeverity": "By Severity",
    "byVertical": "By Vertical",
    "noIssues": "No issues",
    "noIssuesFound": "No issues found",
    "searchPlaceholder": "Search issues…",
    "filtered_one": "{{count}} issue",
    "filtered_other": "{{count}} issues",
    "issueCount_one": "{{count}} issue",
    "issueCount_other": "{{count}} issues",
    "columns": {
      "vertical": "Vertical",
      "severity": "Severity",
      "title": "Title",
      "theme": "Theme",
      "attribution": "Attribution",
      "status": "Status",
      "workspace": "Workspace",
      "details": "Details"
    },
    "attribution": {
      "md": "Model Documentation",
      "mv": "Model Validation",
      "unset": "(unset)"
    },
    "status": {
      "active": "Active",
      "archived": "Archived"
    },
    "openWorkspace": "Open workspace →",
    "openThisWorkspace": "Open this workspace",
    "openWorkspaceAria": "Open workspace {{name}}",
    "expandedAria": "Expanded — click row to collapse",
    "collapsedAria": "Collapsed — click row to expand",
    "details": "Details"
  },
  "pipeline": {
    "searchPlaceholder": "Search workspaces…",
    "noWorkspaceData": "No workspace data",
    "workspaceCount_one": "{{count}} workspace",
    "workspaceCount_other": "{{count}} workspaces",
    "hideArchived": "Hide archived",
    "hideArchivedTooltip": "Archived workspaces are hidden by default. Click to reveal.",
    "showingArchived": "Showing archived",
    "stageBuckets": {
      "new": "New",
      "planning": "Planning",
      "data_prep": "Data Prep",
      "report_gen": "Report Gen",
      "complete": "Complete",
      "archived": "Archived"
    },
    "columns": {
      "workspace": "Workspace",
      "vertical": "Vertical",
      "stage": "Stage",
      "completenessVerdict": "Completeness verdict",
      "issues": "Issues"
    },
    "completeWithErrors": "Complete ⚠",
    "coverageVerdicts": {
      "insufficient": "Insufficient",
      "partial": "Partial",
      "adequate": "Adequate",
      "comprehensive": "Comprehensive",
      "unknown": "Not assessed"
    },
    "verdictTooltip": "Completeness verdict: {{verdict}}",
    "verdictNoCheck": "No completeness check recorded yet.",
    "openWorkspace": "Open workspace",
    "openWorkspaceAria": "Open workspace {{name}}",
    "smeRating": "SME rating: {{value}}",
    "statusTooltip": "Status: {{value}}",
    "statusTooltip_validation": "Validation status: {{value}}",
    "statusTooltip_auditing": "Audit status: {{value}}",
    "statusTooltip_development": "Development status: {{value}}",
    "modal": {
      "createdOn": "Created {{date}}",
      "verticalLabel": "Vertical",
      "stageLabel": "Stage",
      "completenessVerdictLabel": "Completeness verdict",
      "issuesPrompt": "Issues — click a card to filter below",
      "allIssues": "All Issues",
      "filteredIssues": "{{label}} Issues",
      "filterCount": "Showing {{visible}} of {{total}} · Clear filter",
      "noIssuesWorkspace": "No issues recorded for this workspace.",
      "noIssuesFilter": "No issues match the current severity filter.",
      "openWorkspaceAria": "Open workspace",
      "factsLabels": {
        "model": "Model",
        "modelId": "Model ID",
        "version": "Version",
        "scope": "Scope",
        "period": "Period",
        "status": "Status",
        "rating": "Rating"
      }
    },
    "unknownVertical": "Unknown: {{value}}"
  },
  "stage": {
    "unknown": "Unknown: {{stage}}"
  },
  "recentActivity": {
    "unavailable": "Recent activity unavailable: {{error}}",
    "noActivity": "No activity in the last 24 hours"
  },
  "toolboxUsage": {
    "noRuns": "No toolbox runs yet",
    "runsByTool": "Runs by Tool",
    "topFailures": "Top failure causes",
    "runsLabel": "{{label}} ({{count}} runs)",
    "succeeded": "Succeeded",
    "failed": "Failed",
    "cancelled": "Cancelled",
    "stale": "Stale"
  },
  "consumption": {
    "totalTokens": "Total tokens",
    "inputTokens": "Input tokens",
    "outputTokens": "Output tokens",
    "calls": "LLM calls",
    "noData": "No LLM usage recorded this month yet",
    "tokensByModel": "Tokens by Model",
    "dailyTrend": "Daily token trend",
    "topWorkspaces": "Top workspaces",
    "toolboxBucket": "Toolbox (non-workspace)",
    "input": "Input",
    "output": "Output",
    "cache": "Cache"
  },
  "controls": {
    "executive": "Executive",
    "admin": "Admin",
    "scrollToTop": "Scroll to top",
    "scrollToTopAria": "scroll to top"
  },
  "serviceTypeSelector": {
    "withGeneric": "{{count}} · {{generic}} generic",
    "all": "All Service Types",
    "allShort": "All"
  },
  "export": {
    "buttonLabel": "Export",
    "buttonTooltip": "Export current view as Excel workbook (.xlsx)",
    "buttonAria": "Export analytics data to Excel",
    "buttonDisabledTooltip": "Data is still loading",
    "generating": "Generating…",
    "errorTitle": "Export failed",
    "filenamePrefix": "bennie-analytics",
    "sheets": {
      "cover": "Cover",
      "kpi": "KPI Summary",
      "portfolio": "Portfolio",
      "pipeline": "Pipeline",
      "issues": "Issues",
      "toolbox": "Toolbox Usage"
    },
    "cover": {
      "title": "Bennie Analytics Export",
      "generatedAt": "Generated at",
      "snapshotRefreshedAt": "Snapshot refreshed",
      "serviceTypeFilter": "Service Type filter",
      "dateRangeFilter": "Date range",
      "allTime": "All time",
      "coveragePosture": "Coverage posture",
      "coveragePostureBody": "The export always includes the full administrator data set, regardless of the Executive/Admin view.",
      "sheetIndex": "Sheets in this workbook",
      "rowCountSuffix": "({{count}} rows)"
    },
    "kpi": {
      "metric": "Metric",
      "value": "Value",
      "subline": "Notes",
      "totalWorkspaces": "Total Workspaces",
      "totalWorkspaces_validation": "Total Validations",
      "totalWorkspaces_auditing": "Total Audits",
      "totalWorkspaces_development": "Total Developments",
      "completed": "Completed",
      "completedSub": "{{withErrors}} with errors",
      "totalIssues": "Total Issues",
      "issuesSub": "T1:{{t1}} T2:{{t2}} T3:{{t3}} T4:{{t4}}",
      "timeSaved": "Time Saved (hours)",
      "timeSavedSub": "Based on {{measured}} of {{completed}} completed workspaces. Workspaces that already existed when this measurement began are excluded and their finish time is never estimated after the fact."
    },
    "portfolio": {
      "category": "Category",
      "workspaceCount": "Workspace Count",
      "completedCount": "Completed Count",
      "type1": "Type 1",
      "type2": "Type 2",
      "type3": "Type 3",
      "type4": "Type 4"
    },
    "pipeline": {
      "workspaceId": "Workspace ID",
      "name": "Workspace Name",
      "serviceType": "Service Type",
      "vertical": "Vertical",
      "stage": "Stage",
      "coverageVerdict": "Completeness Verdict",
      "type1": "Type 1",
      "type2": "Type 2",
      "type3": "Type 3",
      "type4": "Type 4",
      "createdAt": "Created At",
      "updatedAt": "Updated At",
      "modelName": "Model Name",
      "modelId": "Model ID",
      "scope": "Scope",
      "scope_validation": "Validation Scope",
      "scope_auditing": "Audit Scope",
      "scope_development": "Development Scope",
      "period": "Period",
      "period_validation": "Validation Period",
      "period_auditing": "Audit Period",
      "period_development": "Development Period",
      "rating": "Rating",
      "status": "Status",
      "status_validation": "Validation Status",
      "status_auditing": "Audit Status",
      "status_development": "Development Status",
      "workspaceStatus": "Workspace Status"
    },
    "issues": {
      "id": "Issue ID",
      "workspaceId": "Workspace ID",
      "workspaceName": "Workspace Name",
      "serviceType": "Service Type",
      "severity": "Severity",
      "title": "Title",
      "theme": "Theme",
      "classification": "Classification",
      "attribution": "Attribution",
      "primaryDimension": "Primary Dimension",
      "rootCauseDescription": "Root Cause",
      "supportingSymptoms": "Supporting Symptoms",
      "workspaceStatus": "Workspace Status"
    },
    "toolbox": {
      "toolId": "Tool ID",
      "label": "Label",
      "totalRuns": "Total Runs",
      "succeeded": "Succeeded",
      "failed": "Failed",
      "cancelled": "Cancelled",
      "stale": "Stale",
      "interrupted": "Interrupted",
      "active": "Active",
      "avgDurationMs": "Avg Duration (ms)"
    }
  },
  "verticals": {
    "unresolved": "Vertical not identified"
  }
}
